import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { saveUserLocation, Coordinates } from '../services/locationService';

type LocationContextType = {
  coordinates: Coordinates | null;
  locationStatus: 'prompt' | 'granted' | 'denied';
  requestLocation: () => Promise<void>;
  denyLocation: () => void;
  checkLocationPermission: () => void;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
};

const LocationContext = createContext<LocationContextType>({
  coordinates: null,
  locationStatus: 'prompt',
  requestLocation: async () => {},
  denyLocation: () => {},
  checkLocationPermission: () => {},
  isLoading: false,
  error: null,
  isInitialized: false,
});

export const useLocation = () => useContext(LocationContext);

type LocationProviderProps = {
  children: ReactNode;
};

export const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [locationStatus, setLocationStatus] = useState<'prompt' | 'granted' | 'denied'>('prompt');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Check browser's actual permission status
  const checkBrowserPermission = async (): Promise<PermissionState | null> => {
    try {
      // Check if the Permissions API is supported
      if (navigator.permissions && navigator.permissions.query) {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        return permissionStatus.state as PermissionState;
      }
      return null; // Permissions API not supported
    } catch (error) {
      console.error('Error checking permission:', error);
      return null;
    }
  };

  // Check if location permission is stored in localStorage and verify with browser
  const checkLocationPermission = async () => {
    setIsLoading(true);
    
    try {
      const storedPermission = localStorage.getItem('locationPermission');
      const browserPermission = await checkBrowserPermission();
      
      // If browser permission is denied, override localStorage
      if (browserPermission === 'denied') {
        setLocationStatus('denied');
        localStorage.setItem('locationPermission', 'denied');
        return;
      }
      
      // If browser permission is prompt, override localStorage if it was previously granted
      if (browserPermission === 'prompt' && storedPermission === 'granted') {
        setLocationStatus('prompt');
        localStorage.removeItem('locationPermission');
        localStorage.removeItem('userCoordinates');
        return;
      }
      
      // Otherwise, use the stored permission
      if (storedPermission === 'granted') {
        setLocationStatus('granted');
        // Try to get the stored coordinates
        const storedCoordinates = localStorage.getItem('userCoordinates');
        if (storedCoordinates) {
          try {
            setCoordinates(JSON.parse(storedCoordinates));
          } catch (error) {
            console.error('Error parsing stored coordinates:', error);
          }
        } else {
          // If permission is granted but no coordinates, request them again
          getCurrentPosition();
        }
      } else if (storedPermission === 'denied') {
        setLocationStatus('denied');
      } else {
        setLocationStatus('prompt');
      }
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  // Get current position using Geolocation API
  const getCurrentPosition = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const errorMsg = 'Geolocation is not supported by this browser.';
        console.error(errorMsg);
        setLocationStatus('denied');
        localStorage.setItem('locationPermission', 'denied');
        setError(errorMsg);
        setIsLoading(false);
        reject(new Error(errorMsg));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newCoordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCoordinates(newCoordinates);
          localStorage.setItem('userCoordinates', JSON.stringify(newCoordinates));
          setIsLoading(false);
          resolve();
        },
        (error) => {
          const errorMsg = `Error getting location: ${error.message}`;
          console.error(errorMsg);
          
          // If user denied permission in the browser dialog
          if (error.code === 1) { // PERMISSION_DENIED
            setLocationStatus('denied');
            localStorage.setItem('locationPermission', 'denied');
          }
          
          setError(errorMsg);
          setIsLoading(false);
          reject(error);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 10000, 
          maximumAge: 0 
        }
      );
    });
  };

  // Request location permission and get coordinates
  const requestLocation = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      await getCurrentPosition();
      setLocationStatus('granted');
      localStorage.setItem('locationPermission', 'granted');
      
      // Send location to backend if coordinates are available
      if (coordinates) {
        try {
          await saveUserLocation(coordinates);
        } catch (error: unknown) {
          const errorMsg = error instanceof Error ? error.message : 'Unknown error saving location';
          console.error(errorMsg);
          setError(errorMsg);
          // Don't change location status on API error - we still have the coordinates
        }
      }
    } catch (error: unknown) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error requesting location';
      console.error(errorMsg);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Deny location permission
  const denyLocation = (): void => {
    setLocationStatus('denied');
    localStorage.setItem('locationPermission', 'denied');
  };

  // Check for location permission on mount and periodically
  useEffect(() => {
    checkLocationPermission();

    // Listen for storage events (if user clears localStorage in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'locationPermission' || e.key === 'userCoordinates' || e.key === null) {
        checkLocationPermission();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Send location to backend when coordinates change
  useEffect(() => {
    if (coordinates && locationStatus === 'granted') {
      setIsLoading(true);
      saveUserLocation(coordinates)
        .then(() => {
          setError(null);
        })
        .catch((error: unknown) => {
          const errorMsg = error instanceof Error ? error.message : 'Unknown error saving location';
          console.error('Error saving location to backend:', errorMsg);
          setError(errorMsg);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [coordinates, locationStatus]);

  return (
    <LocationContext.Provider
      value={{
        coordinates,
        locationStatus,
        requestLocation,
        denyLocation,
        checkLocationPermission,
        isLoading,
        error,
        isInitialized,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}; 